import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import Navbar from "../components/Navbar";
import ServiceStatusWidget from "../components/ServiceStatusWidget";
import BulletinBoard from "../components/BulletinBoard";
import CreateTicketModal from "../components/CreateTicketModal";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Ticket, Clock, CheckCircle, AlertCircle, Eye, Shield, ExternalLink, Key, Plus } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export default function Dashboard({ user, logout, previewRole, setPreviewRole, previewDealership, setPreviewDealership, previewDepartment, setPreviewDepartment }) {
  const [stats, setStats] = useState({
    totalTickets: 0,
    newTickets: 0,
    inProgressTickets: 0,
    closedTickets: 0
  });
  const [recentTickets, setRecentTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dealerships, setDealerships] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const navigate = useNavigate();
  
  // Use preview role if admin is previewing, otherwise use actual role
  const effectiveRole = previewRole || user.role;

  // Secure Note state
  const [showSecureNoteModal, setShowSecureNoteModal] = useState(false);
  const [secureNoteData, setSecureNoteData] = useState({
    recipient_email: "",
    credentials_text: "",
    expiration_type: "24_hours"
  });
  const [sendingSecureNote, setSendingSecureNote] = useState(false);

  const handleSendSecureNote = async (e) => {
    e.preventDefault();
    if (!secureNoteData.recipient_email || !secureNoteData.credentials_text) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSendingSecureNote(true);
    try {
      await axios.post(`${API}/secure-credentials`, {
        recipient_email: secureNoteData.recipient_email,
        credentials_text: secureNoteData.credentials_text,
        expiration_type: secureNoteData.expiration_type
      });
      toast.success(`Secure note sent to ${secureNoteData.recipient_email}`);
      setShowSecureNoteModal(false);
      setSecureNoteData({ recipient_email: "", credentials_text: "", expiration_type: "24_hours" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send secure note");
    } finally {
      setSendingSecureNote(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    if (user.role === "admin" || user.role === "super_admin") {
      fetchDealerships();
    }
  }, [effectiveRole, previewDealership]); // Re-fetch when role or dealership changes

  // Auto-refresh dashboard every 60 seconds
  useEffect(() => {
    const autoRefreshInterval = setInterval(() => {
      fetchDashboardData();
    }, 60 * 1000); // 60 seconds

    return () => {
      clearInterval(autoRefreshInterval);
    };
  }, [effectiveRole, previewDealership]);

  const fetchDealerships = async () => {
    try {
      const response = await axios.get(`${API}/dealerships`);
      setDealerships(response.data);
    } catch (e) { /* non-critical */ }
  };

  const fetchDashboardData = async () => {
    try {
      const params = new URLSearchParams();
      if (effectiveRole === "site_admin" && previewDealership) {
        params.append('dealership', previewDealership);
      }
      
      const countsUrl = `${API}/tickets/counts${params.toString() ? '?' + params.toString() : ''}`;
      const countsResponse = await axios.get(countsUrl);
      const counts = countsResponse.data;
      
      setStats({
        totalTickets: counts.total,
        newTickets: counts.new,
        inProgressTickets: counts.in_progress,
        closedTickets: counts.closed
      });
      
      // Fetch only recent tickets for the list
      params.append('limit', '5');
      const ticketsUrl = `${API}/tickets?${params.toString()}`;
      const ticketsResponse = await axios.get(ticketsUrl);
      setRecentTickets((ticketsResponse.data.tickets || []).slice(0, 5));
    } catch (error) {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      new: "bg-blue-100 text-blue-700",
      in_progress: "bg-purple-100 text-purple-700",
      pending: "bg-yellow-100 text-yellow-700",
      closed: "bg-gray-100 text-gray-700"
    };
    return classes[status] || classes.new;
  };

  const getPriorityBadgeClass = (priority) => {
    const classes = {
      low: "bg-green-100 text-green-700",
      medium: "bg-yellow-100 text-yellow-700",
      high: "bg-orange-100 text-orange-700",
      critical: "bg-red-100 text-red-700"
    };
    return classes[priority] || classes.medium;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-[#252525]">
        <Navbar user={user} logout={logout} previewRole={previewRole} setPreviewRole={setPreviewRole} />
        <div className="flex items-center justify-center h-96">
          <div className="text-lg text-slate-60 dark:text-white0 dark:text-white">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#252525]">
      <Navbar user={user} logout={logout} effectiveRole={effectiveRole} previewRole={previewRole} setPreviewRole={setPreviewRole} previewDealership={previewDealership} setPreviewDealership={setPreviewDealership} previewDepartment={previewDepartment} setPreviewDepartment={setPreviewDepartment} />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-80 dark:text-white0 dark:text-white" data-testid="dashboard-title">
              Welcome back, {user.name}!
            </h1>
            <p className="text-slate-60 dark:text-white0 dark:text-white mt-1">Here's your ticket overview</p>
          </div>
          <Button
            onClick={() => setShowCreateModal(true)}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="dashboard-create-ticket-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Submit Ticket
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8">
          <Card 
            className="border-0 shadow-md hover:shadow-lg transition-shadow cursor-pointer"
            onClick={() => navigate('/tickets')}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-60 dark:text-white0 dark:text-white">Total Tickets</p>
                  <p className="text-3xl font-bold text-slate-80 dark:text-white0 dark:text-white mt-2" data-testid="total-tickets-count">
                    {stats.totalTickets}
                  </p>
                </div>
                <div className="bg-blue-100 p-3 rounded-lg">
                  <Ticket className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className="border-0 shadow-md hover:shadow-lg transition-shadow cursor-pointer"
            onClick={() => navigate('/tickets?status=new')}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-60 dark:text-white0 dark:text-white">New Tickets</p>
                  <p className="text-3xl font-bold text-blue-600 mt-2" data-testid="new-tickets-count">
                    {stats.newTickets}
                  </p>
                </div>
                <div className="bg-blue-100 p-3 rounded-lg">
                  <AlertCircle className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className="border-0 shadow-md hover:shadow-lg transition-shadow cursor-pointer"
            onClick={() => navigate('/tickets?status=in_progress')}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-60 dark:text-white0 dark:text-white">In Progress</p>
                  <p className="text-3xl font-bold text-purple-600 mt-2" data-testid="inprogress-tickets-count">
                    {stats.inProgressTickets}
                  </p>
                </div>
                <div className="bg-purple-100 p-3 rounded-lg">
                  <Clock className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className="border-0 shadow-md hover:shadow-lg transition-shadow cursor-pointer"
            onClick={() => navigate('/knowledge-base')}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600 dark:text-white">Closed</p>
                  <p className="text-3xl font-bold text-gray-600 mt-2" data-testid="closed-tickets-count">
                    {stats.closedTickets}
                  </p>
                </div>
                <div className="bg-gray-100 p-3 rounded-lg">
                  <CheckCircle className="w-6 h-6 text-gray-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Send Secure Note & Secure Email */}
        <Card className="border-0 shadow-md mb-8" data-testid="secure-email-card">
          <CardContent className="p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center shrink-0">
                  <Shield className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800 dark:text-white">Secure Communications</h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Send encrypted notes and emails securely</p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
                <Button
                  onClick={() => setShowSecureNoteModal(true)}
                  className="bg-[#7a1d42] hover:bg-[#5a1632] gap-2 flex-1 sm:flex-none"
                  data-testid="send-secure-note-btn"
                >
                  <Key className="w-4 h-4" />
                  Send Secure Note
                </Button>
                <a
                  href="https://complycrypt.complyauto.com/p/link/5e464851-e93a-414d-a3b4-d30eb14a2c71"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 sm:flex-none"
                  data-testid="secure-email-btn"
                >
                  <Button className="bg-emerald-600 hover:bg-emerald-700 gap-2 w-full">
                    <ExternalLink className="w-4 h-4" />
                    Open ComplyCrypt
                  </Button>
                </a>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Bulletin Board */}
        <div className="mb-8">
          <BulletinBoard user={{...user, role: effectiveRole}} />
        </div>

        {/* Service Status Widget */}
        <div className="mb-8">
          <ServiceStatusWidget />
        </div>

        {/* Recent Tickets */}
        <Card className="border-0 shadow-md">
          <CardHeader className="border-b border-slate-100">
            <div className="flex items-center justify-between">
              <CardTitle>Recent Tickets</CardTitle>
              <Link to="/tickets">
                <Button variant="outline" size="sm" data-testid="view-all-tickets-button">
                  View All
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            {recentTickets.length === 0 ? (
              <p className="text-center text-slate-500 dark:text-white py-8">No tickets yet</p>
            ) : (
              <div className="space-y-4">
                {recentTickets.map((ticket) => (
                  <div
                    key={ticket.id}
                    className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
                    onClick={() => navigate(`/tickets/${ticket.id}`)}
                    data-testid={`ticket-item-${ticket.id}`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <span className="font-mono text-sm font-semibold text-slate-70 dark:text-white0">
                          {ticket.ticket_number}
                        </span>
                        <Badge className={getPriorityBadgeClass(ticket.priority)}>
                          {ticket.priority}
                        </Badge>
                      </div>
                      <p className="font-medium text-slate-80 dark:text-white0 dark:text-white">{ticket.title}</p>
                      <p className="text-sm text-slate-500 dark:text-white mt-1">
                        {new Date(ticket.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <Badge className={getStatusBadgeClass(ticket.status)}>
                      {ticket.status.replace('_', ' ')}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Send Secure Note Modal */}
      <Dialog open={showSecureNoteModal} onOpenChange={setShowSecureNoteModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5" />
              Send Secure Note
            </DialogTitle>
            <DialogDescription>
              Send encrypted credentials or notes via a secure, expiring link.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSendSecureNote} className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="secure-note-email">Recipient Email</Label>
              <Input
                id="secure-note-email"
                type="email"
                placeholder="recipient@example.com"
                value={secureNoteData.recipient_email}
                onChange={(e) => setSecureNoteData(prev => ({ ...prev, recipient_email: e.target.value }))}
                data-testid="secure-note-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="secure-note-text">Credentials / Note</Label>
              <Textarea
                id="secure-note-text"
                placeholder="Enter username, password, or other secure information..."
                value={secureNoteData.credentials_text}
                onChange={(e) => setSecureNoteData(prev => ({ ...prev, credentials_text: e.target.value }))}
                rows={4}
                data-testid="secure-note-text-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="secure-note-expiration">Link Expiration</Label>
              <Select
                value={secureNoteData.expiration_type}
                onValueChange={(value) => setSecureNoteData(prev => ({ ...prev, expiration_type: value }))}
              >
                <SelectTrigger data-testid="secure-note-expiration-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="one_time">One-time view (expires after viewing)</SelectItem>
                  <SelectItem value="24_hours">24 hours</SelectItem>
                  <SelectItem value="7_days">7 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowSecureNoteModal(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={sendingSecureNote || !secureNoteData.recipient_email || !secureNoteData.credentials_text}
                className="bg-[#7a1d42] hover:bg-[#5a1632]"
                data-testid="secure-note-send-btn"
              >
                {sendingSecureNote ? "Sending..." : "Send Secure Note"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {showCreateModal && (
        <CreateTicketModal
          user={user}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            fetchDashboardData();
            toast.success("Ticket created successfully!");
          }}
        />
      )}
    </div>
  );
}